import dayjs, { Dayjs } from 'dayjs';

type Props = {
	date: string | Dayjs;
	workTime: {
		date: string;
		from: string;
		till: string;
	};
};

/**
 * Checks if a given date falls within the specified work time.
 *
 * @param {Props} props - The properties object containing the date and work time.
 * @param {string | Dayjs} props.date - The date to check, which can be a string or a Dayjs object.
 * @param {Object} props.workTime - An object representing the work time.
 * @param {string} props.workTime.date - The date for the work time in 'YYYY-MM-DD' format.
 * @param {string} props.workTime.from - The start time of the work period in 'HH:mm' format.
 * @param {string} props.workTime.till - The end time of the work period in 'HH:mm' format.
 *
 * @returns {boolean} - Returns true if the given date is within the work time,
 *                      false otherwise. If the work time starts after it ends,
 *                      it assumes the work period spans into the next day.
 *
 * @example
 * const isWithinWorkTime = getIsDateBetweenWorkTime({
 *   date: '2023-10-01T10:00:00',
 *   workTime: {
 *     date: '2023-10-01',
 *     from: '09:00',
 *     till: '17:00'
 *   }
 * });
 * console.log(isWithinWorkTime); // true
 */
export const getIsDateBetweenWorkTime = (props: Props): boolean => {
	const date = dayjs(props.date);
	const workTime = {
		from: dayjs(`${props.workTime.date} ${props.workTime.from}`),
		till: dayjs(`${props.workTime.date} ${props.workTime.till}`),
	};

	if (workTime.from.isAfter(workTime.till)) {
		workTime.till = workTime.till.add(1, 'days');
	}

	return (
		(date.isAfter(workTime.from) || date.isSame(workTime.from)) && date.isBefore(workTime.till)
	);
};
